import { START_LOCATION, type NavigationFailure, type RouteLocationNormalized } from 'vue-router'
import { telemetryEvents } from '@/telemetry/eventNames'

const UNMATCHED_ROUTE_PATTERN = 'unmatched'
const ALLOWED_QUERY_KEYS = ['tab', 'widget'] as const

export type CaptureFn = (name: string, properties: Record<string, unknown>) => void

export type PageviewHook = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  failure?: NavigationFailure | void
) => void

// Emits the matched route pattern and never the resolved URL: record identifiers
// (caseId, consultationId, consultationShareCode and four others) live in the path,
// and the share code in particular grants call access.
export function createPageviewHook(capture: CaptureFn): PageviewHook {
  let lastPayload: string | undefined

  return (to, from, failure) => {
    if (failure) {
      return
    }

    const properties: Record<string, unknown> = {
      route_name: typeof to.name === 'string' ? to.name : '',
      route_pattern: to.matched.at(-1)?.path ?? UNMATCHED_ROUTE_PATTERN,
      navigation_type: from === START_LOCATION ? 'initial' : 'in_app',
      ...allowedQuery(to)
    }

    const payload = JSON.stringify(properties)
    if (payload === lastPayload) {
      return
    }
    lastPayload = payload

    capture(telemetryEvents.PAGE_VIEWED, properties)
  }
}

function allowedQuery(to: RouteLocationNormalized): Record<string, string> {
  const result: Record<string, string> = {}

  for (const key of ALLOWED_QUERY_KEYS) {
    const value = to.query[key]
    if (typeof value === 'string') {
      result[key] = value
    }
  }

  return result
}
